import React, { useState, useMemo } from 'react';
import { Modal, List, Input, Form, Typography } from 'antd';
import styles from './CampaignLogModal.module.css';
import type { LogEntry } from '../../state/generalActions';

interface CampaignLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignLog: LogEntry[];
  gameTime: string | null;
  onAddLogEntry: (text: string) => void;
}

const formatLogTimestamp = (isoString: string) => {
    const date = new Date(isoString);
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `Day ${day}, ${hours}:${minutes}`;
};

const CampaignLogModal: React.FC<CampaignLogModalProps> = ({
  isOpen,
  onClose,
  campaignLog,
  gameTime,
  onAddLogEntry,
}) => {
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');

  const handleAdd = () => {
    form.validateFields().then(values => {
      const newText = values.newLogText?.trim(); // Optional chaining prevents an error if newLogText is undefined.
      if (newText) {
        onAddLogEntry(newText);
        form.resetFields();
      }
    });
  };

  const filteredLog = useMemo(() => {
    const reversedLog = [...(campaignLog || [])].reverse(); // Show newest entries first
    if (!searchTerm) {
      return reversedLog;
    }
    const lowercasedFilter = searchTerm.toLowerCase();
    return (reversedLog ?? []).filter(item =>
      item.text.toLowerCase().includes(lowercasedFilter) ||
      (item.user?.username?.toLowerCase().includes(lowercasedFilter))
    );
  }, [campaignLog, searchTerm]);

  return (
    <Modal
      title="Campaign Log"
      open={isOpen}
      onCancel={onClose}
      onOk={handleAdd}
      okText="Add Entry"
      width={600}
    >
      <Input.Search
        placeholder="Search log entries..."
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: '1rem' }}
        allowClear
      />
      <List
        dataSource={filteredLog}
        className={styles.list}
        renderItem={(item: LogEntry) => (
          <List.Item>
            <div className={styles.logItem}>
              <span className={styles.timestamp}>{formatLogTimestamp(item.timestamp)}</span>
              <span className={styles.logText}>{item.text}</span>
              {item.user && <span className={styles.username}>- {item.user.username}</span>}
            </div>
          </List.Item>
        )}
      />
      <div className={styles.newEntrySection}>
        <Typography.Text type="secondary" className={styles.newTimestampInfo}>
          New entry will be recorded at game time: {gameTime ? formatLogTimestamp(gameTime) : '...'}
        </Typography.Text>
        <Form form={form} onFinish={handleAdd}>
          <Form.Item name="newLogText" noStyle>
            <Input.TextArea rows={2} placeholder="Add manual entry..." />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default CampaignLogModal;